import './panel-fonts.css';
import './panels.css';
import { CAL_LINK, CV, EDUCATION, OWNER, WORLD_TECH, type PanelId } from './content';
import { icon } from './computer/icons';
import { JOURNEY, PROFILE, PROJECTS, type Project } from './computer/data';
import { GAME } from '../features';

// Studio panels: what opens when you click the portrait, the thesis, the phone, or the studio menu.
//
// Research behind the layout: NN/g on bottom sheets ("should not be used for displaying complex content") and on
// modal vs non-modal dialogs; NN/g on portfolios (hiring managers scan; a few strong projects with visuals and impact).
// Behind the look: the owner's brief - not the template look of AI-made sites (a sans headline with one italic serif
// word, pill badges, rows of big stat numbers, a gold button everywhere) but a creative-developer one:
//  - type: a condensed variable grotesk for display (Bricolage Grotesque, width and optical-size axes) and a mono for
//    labels and data (JetBrains Mono); no serif italics;
//  - a Swiss grid with hairline rules instead of boxed cards; facts as a spec sheet, not stat tiles; each chapter's
//    Bangla name appears small in the link to it;
//  - a few deliberate interactions: headings rise word by word out of a mask, mono labels scramble on hover, the
//    project index shows a preview that follows the cursor.
// Desktop: a wide reading surface (60% of the screen, up to 1040 px); the focused object in the room stays in view
// beside it. Phones: a bottom sheet (grab handle, swipe down, visible ×), and the Back button closes it.
// Each section has its own information hierarchy:
//   About      the portrait, the name set big, the story, a spec sheet, what I'm doing now;
//   Work       a numbered index and the selected project large, each project in its own colour;
//   Skills     three capabilities, each backed by projects you can open, tools as a plain list;
//   Education  BRAC University big, a 2023-2027 ruler with today marked, the path in a table, the thesis;
//   Contact    the email and WhatsApp number as the headline, Dhaka's local time, one primary action.

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
const ext = (href: string, label: string, cls = 'sp-link') => `<a class="${cls}" href="${esc(href)}" target="_blank" rel="noopener"><span data-scramble>${label}</span><span class="arr" aria-hidden="true">↗</span></a>`;
const act = (action: string, label: string, cls = 'sp-link') => `<button class="${cls}" data-action="${action}"><span data-scramble>${label}</span><span class="arr" aria-hidden="true">→</span></button>`;
/** The CV: open it in the browser's PDF viewer, or download it. */
const cvLinks = () => `<a class="sp-link" href="${CV.href}" target="_blank" rel="noopener"><span data-scramble>View CV</span><span class="arr" aria-hidden="true">↗</span></a><a class="sp-link" href="${CV.href}" download="${CV.file}"><span data-scramble>Download CV</span><span class="arr" aria-hidden="true">↓</span></a>`;
/** A display heading whose words rise out of a mask one after another. */
const rise = (text: string, cls = '', id = '') => `<h2${id ? ` id="${id}"` : ''} class="sp-rise ${cls}">${text.split(' ').map((w, i) => `<span class="w"><span style="--i:${i}">${esc(w)}</span></span>`).join(' ')}</h2>`;

export const whatsappHref = `https://wa.me/${OWNER.whatsapp}?text=${encodeURIComponent('Hi Salman! I found you through your Dhaka portfolio.')}`;
export const mailHref = `mailto:${OWNER.email}?subject=${encodeURIComponent('Hello from your Dhaka portfolio')}`;

/** The panel's width on this screen (studio.ts frames the focused object in the space left of it). */
export function panelWidth() {
  const w = innerWidth;
  return w > 1100 ? Math.min(w * 0.6, 1040) : w > 700 ? Math.min(w * 0.64, 620) : 0;
}

// ------------------------------------------------------------------ chapters

const CHAPTERS: Array<[PanelId, string, string]> = [
  ['about', 'About', 'পরিচয়'], ['work', 'Work', 'কাজ'], ['skills', 'Skills', 'দক্ষতা'], ['education', 'Education', 'শিক্ষা'], ['contact', 'Contact', 'যোগাযোগ'],
];
const chapterOf = (id: PanelId): PanelId => (id === 'booking' ? 'contact' : id);
const num = (id: PanelId) => String(CHAPTERS.findIndex(([c]) => c === chapterOf(id)) + 1).padStart(2, '0');
const label = (id: PanelId, text: string) => `<p class="sp-label"><span>[${num(id)}]</span><span data-scramble>${esc(text)}</span></p>`;
function next(id: PanelId) {
  const i = CHAPTERS.findIndex(([c]) => c === id);
  if (i < 0 || i === CHAPTERS.length - 1) return '';
  const [n, name, bn] = CHAPTERS[i + 1];
  return `<button class="sp-next" data-panel="${n}"><span class="k">Next [${num(n)}]</span><span class="t">${esc(name)}</span><span class="bn" lang="bn">${bn}</span><span class="arr" aria-hidden="true">→</span></button>`;
}

// ------------------------------------------------------------------ work: each project in its own colour

const LOOK: Record<string, { accent: string; year?: string; shot?: string }> = {
  nongorr: { accent: '#d0566b', year: '2025–26' },
  battlezone: { accent: '#4f8dff', year: '2026' },
  jarvis: { accent: '#a29cff' },
  starline: { accent: '#f2d27a', year: '2025–26' },
  blood: { accent: '#ec6450', year: '2026' },
  'gnn-bert': { accent: '#5fc8bf' },
  kuasha: { accent: '#93c070' },
  naeem: { accent: '#aab6c3' },
  dhaka: { accent: '#e9b45e', year: '2026', shot: '/os/gallery/road-300feet.webp' },
};
const look = (p: Project) => LOOK[p.id] ?? { accent: '#e9b45e' };
const shotOf = (p: Project) => p.shot ?? look(p).shot;
const visual = (p: Project) => {
  const shot = shotOf(p);
  return shot
    ? `<img src="${esc(shot)}" alt="${esc(p.name)}, screenshot" loading="lazy" decoding="async" />`
    : `<div class="wk-poster" aria-hidden="true"><span>${esc(p.kicker)}</span><b>${esc(p.name)}</b></div>`;
};

function projectView(p: Project, k: number) {
  const l = look(p);
  return `<div class="wk-detail" style="--acc:${l.accent}">
    <figure class="wk-shot">${visual(p)}</figure>
    <div class="wk-head"><span class="wk-n">${String(k + 1).padStart(2, '0')}</span><h3>${esc(p.name)}</h3></div>
    <dl class="sp-spec">
      <div><dt>Type</dt><dd>${esc(p.kicker)}</dd></div>
      <div><dt>Status</dt><dd><i class="sw"></i>${esc(p.status)}</dd></div>
      ${l.year ? `<div><dt>Year</dt><dd>${esc(l.year)}</dd></div>` : ''}
      ${p.role ? `<div><dt>Role</dt><dd>${esc(p.role)}</dd></div>` : ''}
      <div><dt>Stack</dt><dd>${p.stack.map(esc).join(', ')}</dd></div>
    </dl>
    <p class="wk-sum">${esc(p.summary)}</p>
    ${p.points.length ? `<ul class="wk-points">${p.points.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
    <div class="sp-actions">${p.links.length ? p.links.map((x) => ext(x.href, esc(x.label))).join('') : '<span class="sp-quiet">Not public yet</span>'}</div>
  </div>`;
}

// ------------------------------------------------------------------ helpers

function degreeProgress() {
  const start = Date.UTC(2023, 0, 1), end = Date.UTC(2027, 11, 31);
  return Math.min(1, Math.max(0, (Date.now() - start) / (end - start)));
}
const dhakaTime = () => new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Dhaka', hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();

// the channels' own marks (official glyphs: Simple Icons for WhatsApp and GitHub, Google's four-colour Gmail logo)
const WA_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z';
const LOGO = {
  whatsapp: (fill = '#25D366') => `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${fill}" d="${WA_PATH}"/></svg>`,
  gmail: () => '<svg viewBox="52 42 88 66" aria-hidden="true"><path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6"/><path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15"/><path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2"/><path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92"/><path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2"/></svg>',
  github: () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>',
};

function channels() {
  const row = (logo: string, name: string, value: string, href: string, copy: string, cls = '') => `<div class="ct-row ${cls}">
      <a class="ct-logo" href="${esc(href)}" target="_blank" rel="noopener" aria-label="${esc(name)}">${logo}</a>
      <div class="ct-id"><span class="ct-k">${esc(name)}</span><a class="ct-v" href="${esc(href)}" target="_blank" rel="noopener">${esc(value)}</a></div>
      <span class="ct-act"><button data-copy="${esc(copy)}" aria-label="Copy ${esc(name)}">${icon('copy')}</button><a href="${esc(href)}" target="_blank" rel="noopener" aria-label="Open ${esc(name)}">${icon('arrowUpRight')}</a></span>
    </div>`;
  return `<div class="ct-list">
    ${row(LOGO.whatsapp(), 'WhatsApp', OWNER.whatsappDisplay, whatsappHref, OWNER.whatsappDisplay, 'wa')}
    ${row(LOGO.gmail(), 'Gmail', OWNER.email, mailHref, OWNER.email)}
    ${row(LOGO.github(), 'GitHub', 'github.com/kazisalman21', OWNER.github, OWNER.github)}
  </div>`;
}

// While the game is not live (features.ts) every way into the city opens this: what it is, real shots from the build,
// and a way to hear when it launches.
const launchHref = `https://wa.me/${OWNER.whatsapp}?text=${encodeURIComponent('Hi Salman! Tell me when Salman’s Dhaka, the game, launches.')}`;
function comingSoon() {
  const shot = (src: string, alt: string) => `<figure><img src="${src}" alt="${esc(alt)}" loading="lazy" decoding="async" /></figure>`;
  return `<header class="sp-head"><p class="sp-label"><span>[✦]</span><span data-scramble>The game</span></p>${rise('Coming soon', 'sp-display', 'panel-title')}
      <p class="ct-status"><i class="sw on"></i>In development · Salman’s Dhaka, a city you can drive</p></header>
    <div class="gm-shots">${shot('/os/gallery/road-bridge.webp', 'Hatirjheel’s white arch bridges in the game')}${shot('/os/gallery/road-300feet.webp', 'The 300 Feet road in the game')}${shot('/os/gallery/bus.webp', 'A BRTC bus in the game')}</div>
    <p class="ab-lead">A stylised Dhaka you can drive through, built from scratch: modelled in Blender, running in your browser.</p>
    <dl class="sp-spec">
      <div><dt>Drive</dt><dd>A Toyota Corolla or a BRTC bus, on real physics</dd></div>
      <div><dt>Roads</dt><dd>Hatirjheel’s arch bridges, the Kuril Flyover, the Purbachal cloverleaf, the 300 Feet road</dd></div>
      <div><dt>Traffic</dt><dd>CNGs, rickshaws and buses that follow the junction rules</dd></div>
      <div><dt>Status</dt><dd><i class="sw on"></i>In development · launching soon</dd></div>
    </dl>
    <div class="sp-actions">
      <a class="sp-primary" href="${esc(launchHref)}" target="_blank" rel="noopener">${LOGO.whatsapp('currentColor')}<span data-scramble>Tell me when it launches</span><span class="arr" aria-hidden="true">↗</span></a>
      ${act('computer:gallery', 'Behind-the-build gallery')}<button class="sp-link" data-panel="work"><span data-scramble>See my work</span><span class="arr" aria-hidden="true">→</span></button>
    </div>`;
}
// ------------------------------------------------------------------ the pages

const RENDER: Record<PanelId, (arg?: string) => string> = {
  about: () => `<div class="ab-grid">
      <figure class="ab-photo"><img src="/studio/portrait.webp" alt="Kazi Salman, smiling, in a black suit" decoding="async" /><figcaption><span>Dhaka, BD</span><span>23.78° N, 90.40° E</span></figcaption></figure>
      <div class="ab-text">
        ${label('about', 'About')}
        ${rise('Kazi Salman', 'sp-display', 'panel-title')}
        <p class="ab-role">Software engineer · Full-stack · AI</p>
        <p class="ab-lead">I build web products people use, and ${GAME ? 'I built this city you can drive' : 'I’m building a city you can drive'}.</p>
        <p>My work is the kind people pay for and use every day: <button class="sp-inline" data-goto="work:nongorr">Nongorr</button>, an online store for a Bangladeshi fashion brand, and <button class="sp-inline" data-goto="work:battlezone">Battle Zone BD</button>, a Free Fire tournament platform. On the AI side I’m building a local-first assistant and writing my thesis on AI for cervical screening.</p>
        <p>The studio you’re standing in and the streets outside are mine too: every car, road and lamp was modelled, lit and coded piece by piece.</p>
        <dl class="sp-spec">
          <div><dt>Based in</dt><dd>Dhaka, Bangladesh</dd></div>
          <div><dt>Studying</dt><dd>Computer Science, BRAC University · ${esc(PROFILE.semester)}</dd></div>
          <div><dt>Graduating</dt><dd>2027</dd></div>
          <div><dt>Shipping since</dt><dd>2024</dd></div>
          <div><dt>Running</dt><dd><i class="sw on"></i>Nongorr · live</dd></div>
          <div><dt>Next</dt><dd>Battle Zone BD launch, Starline</dd></div>
        </dl>
        <div class="sp-actions">${act('computer:work', 'Start a project', 'sp-link strong')}${cvLinks()}<a class="sp-link" href="/about"><span data-scramble>Read as a page</span><span class="arr" aria-hidden="true">→</span></a></div>
      </div>
    </div>${next('about')}`,

  work: (arg) => {
    const i = Math.max(0, PROJECTS.findIndex((p) => p.id === arg));
    return `<header class="sp-head">${label('work', 'Selected work 2024–2026')}${rise('Built and shipped', 'sp-display', 'panel-title')}</header>
      <div class="wk-grid">
        <ol class="wk-index" role="tablist" aria-label="Projects">${PROJECTS.map((p, k) => `<li><button role="tab" data-project="${p.id}" aria-selected="${k === i}" style="--acc:${look(p).accent}">
            <span class="n">${String(k + 1).padStart(2, '0')}</span><span class="t">${esc(p.name)}</span><span class="s" data-scramble>${esc(p.status)}</span></button></li>`).join('')}</ol>
        <section class="wk-view" role="tabpanel" aria-live="polite">${projectView(PROJECTS[i], i)}</section>
      </div>
      <div class="wk-float" aria-hidden="true"></div>
      <div class="sp-actions">${act('computer:projects', 'Every case study on the computer')}${ext(OWNER.github, 'GitHub')}</div>
      ${next('work')}`;
  },

  skills: () => {
    const caps = [
      { name: 'Web engineering', line: 'Full-stack products that take orders and run a business: storefronts, platforms and dashboards that stay fast on a phone.', proof: ['nongorr', 'battlezone', 'blood'], tools: ['TypeScript', 'JavaScript', 'React', 'Node.js', 'PHP', 'PostgreSQL', 'Astro', 'Tailwind CSS'] },
      { name: 'Real-time 3D', line: 'Interactive worlds in the browser: real physics, traffic that follows the rules, baked lighting and live sound. You are standing in one.', proof: ['dhaka'], tools: ['Three.js', 'Rapier physics', 'Blender + Python', 'Web Audio', 'Vite'] },
      { name: 'AI & research', line: 'Applied machine learning, from a local assistant that routes work between models to neural-network research and a thesis in healthcare AI.', proof: ['jarvis', 'gnn-bert'], tools: ['Python', 'PyTorch', 'Graph neural nets', 'BERT', 'Ollama'] },
    ];
    const name = (id: string) => PROJECTS.find((p) => p.id === id)?.name ?? id;
    return `<header class="sp-head">${label('skills', 'Capabilities')}${rise('Three things I do well', 'sp-display', 'panel-title')}</header>
      <ol class="sk-list">${caps.map((c, k) => `<li>
          <span class="sk-n">${String(k + 1).padStart(2, '0')}</span>
          <div class="sk-main"><h3>${esc(c.name)}</h3><p>${esc(c.line)}</p>
            <p class="sk-proof"><span>Proof →</span>${c.proof.map((id) => `<button class="sp-inline" data-goto="work:${id}">${esc(name(id))}</button>`).join('')}${k === 2 ? '<button class="sp-inline" data-panel="education">Thesis</button>' : ''}</p></div>
          <ul class="sk-tools">${c.tools.map((t) => `<li>${esc(t)}</li>`).join('')}</ul>
        </li>`).join('')}</ol>
      <div class="sp-actions">${act('computer:skills', 'Where I used each tool')}${act('computer:services', 'What I can build for you')}</div>
      ${next('skills')}`;
  },

  education: () => {
    const f = degreeProgress();
    const steps = JOURNEY.filter((j) => j.kind === 'education').reverse();
    return `<header class="sp-head">${label('education', 'Education')}${rise(EDUCATION.school, 'sp-display', 'panel-title')}
        <p class="ed-sub">${esc(EDUCATION.degree)} · ${esc(EDUCATION.place)}</p></header>
      <div class="ed-ruler" style="--f:${f.toFixed(3)}" role="img" aria-label="Degree progress: ${esc(PROFILE.semester)}, graduating 2027">
        <div class="ticks">${Array.from({ length: 21 }, (_, k) => `<i class="${k % 4 === 0 ? 'y' : ''}"></i>`).join('')}</div>
        <div class="fill"></div>
        <span class="now">▲ Now · ${esc(PROFILE.semester.replace(' semester', ''))}</span>
        <div class="years">${[2023, 2024, 2025, 2026, 2027].map((y) => `<span>${y}</span>`).join('')}</div>
      </div>
      <table class="ed-table"><tbody>${steps.map((j) => `<tr class="${j.state ?? ''}"><td class="y">${esc(j.when)}</td><td class="e">${esc(j.title)}</td><td class="o">${esc(j.org)}</td><td class="st">${j.state === 'now' ? 'In progress' : j.state === 'next' ? 'Ahead' : 'Done'}</td></tr>`).join('')}</tbody></table>
      <div class="ed-grid">
        <div class="ed-thesis"><p class="sp-label"><span>Thesis</span><span>${esc(EDUCATION.thesisId)}</span></p><p class="t">${esc(EDUCATION.thesis)}</p></div>
        <div class="ed-courses"><p class="sp-label"><span>Course projects</span></p>${EDUCATION.courses.map((c) => { const [code, title] = c.split(' · '); return `<p><b>${esc(code)}</b><span>${esc(title ?? '')}</span></p>`; }).join('')}</div>
      </div>
      <div class="sp-actions">${cvLinks()}${act('computer:career', 'My full journey')}</div>
      ${next('education')}`;
  },

  contact: () => `<header class="sp-head">${label('contact', 'Contact')}${rise('Say hello', 'sp-display', 'panel-title')}
      <p class="ct-status"><i class="sw on"></i>Taking new projects · replies within a day · <span data-clock>${dhakaTime()}</span> in Dhaka</p></header>
    <a class="ct-big" href="${esc(mailHref)}">${esc(OWNER.email)}</a>
    ${channels()}
    <div class="sp-actions">
      <a class="sp-primary" href="${esc(whatsappHref)}" target="_blank" rel="noopener">${LOGO.whatsapp('currentColor')}<span data-scramble>Message on WhatsApp</span><span class="arr" aria-hidden="true">↗</span></a>
      ${act('computer:work', 'Send a project brief')}${cvLinks()}<button class="sp-link" data-panel="booking"><span data-scramble>Book a call</span><span class="arr" aria-hidden="true">→</span></button>
    </div>`,

  booking: () => `<header class="sp-head">${label('booking', 'Book a call')}${rise(CAL_LINK ? 'Pick a time' : 'Find a time', 'sp-display', 'panel-title')}
      <p class="ct-status">${CAL_LINK ? 'A short video call about your project.' : 'Online booking opens soon. Send two or three times that suit you and I’ll reply with a Google Meet link.'} · <span data-clock>${dhakaTime()}</span> in Dhaka</p></header>
    ${CAL_LINK ? `<iframe class="cal" title="Book a call with ${esc(OWNER.name)}" loading="lazy" src="https://cal.com/${esc(CAL_LINK)}?embed=true&theme=dark&layout=month_view"></iframe>` : ''}
    ${channels()}
    <div class="sp-actions"><a class="sp-primary" href="${esc(whatsappHref)}" target="_blank" rel="noopener">${LOGO.whatsapp('currentColor')}<span data-scramble>Message on WhatsApp</span><span class="arr" aria-hidden="true">↗</span></a>${act('computer:work', 'Send a project brief')}</div>`,

  world: () => `<header class="sp-head"><p class="sp-label"><span>[✦]</span><span data-scramble>Behind the scenes</span></p>${rise('How this world is built', 'sp-display', 'panel-title')}</header>
    <ol class="sk-list wd">${WORLD_TECH.map(([k, v], i) => `<li><span class="sk-n">${String(i + 1).padStart(2, '0')}</span><div class="sk-main"><h3>${esc(k)}</h3><p>${esc(v)}</p></div></li>`).join('')}</ol>
    <div class="sp-actions">${act('computer:gallery', 'Behind-the-build gallery')}${act('exit', GAME ? 'Drive the city' : 'The game · coming soon')}</div>`,

  drive: () => (GAME ? `<header class="sp-head"><p class="sp-label"><span>[✦]</span><span data-scramble>The city</span></p>${rise('Dhaka is waiting outside', 'sp-display', 'panel-title')}</header>
    <p class="ab-lead">Take the Corolla or a BRTC bus across Hatirjheel’s arch bridges, through the Kuril Flyover and round the Purbachal cloverleaf.</p>
    <div class="sp-actions"><button class="sp-primary" data-action="exit"><span data-scramble>Drive</span><span class="arr" aria-hidden="true">→</span></button></div>` : comingSoon()),
};

// ------------------------------------------------------------------ text scramble (mono labels, on hover)

const GLYPHS = '!<>-_\\/[]{}=+*^?#01';
function scramble(el: HTMLElement) {
  if (el.dataset.busy || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const final = el.dataset.text ?? (el.dataset.text = el.textContent ?? '');
  el.dataset.busy = '1';
  let frame = 0;
  const total = Math.min(18, final.length + 6);
  const tick = () => {
    const done = Math.floor((frame / total) * final.length);
    el.textContent = final.split('').map((c, i) => (i < done || c === ' ' ? c : GLYPHS[(Math.random() * GLYPHS.length) | 0])).join('');
    if (++frame <= total) requestAnimationFrame(tick); else { el.textContent = final; delete el.dataset.busy; }
  };
  tick();
}

// ------------------------------------------------------------------ the panel

export class Panels {
  private root: HTMLElement;
  private body: HTMLElement;
  private lastFocus: Element | null = null;
  private clock = 0;
  private pushed = false;
  current: PanelId | null = null;
  onClose: () => void = () => {};
  onAction: (action: string) => void = () => {};

  constructor() {
    this.root = document.getElementById('studio-panel') as HTMLElement;
    this.body = document.getElementById('panel-body') as HTMLElement;
    const closeBtn = document.getElementById('panel-close') as HTMLElement;
    closeBtn.innerHTML = icon('x');
    closeBtn.addEventListener('click', () => this.close());
    this.root.addEventListener('click', async (e) => {
      const t = (e.target as HTMLElement).closest<HTMLElement>('[data-panel],[data-action],[data-copy],[data-goto],[data-project]');
      if (!t) return;
      if (t.dataset.copy) {
        try { await navigator.clipboard.writeText(t.dataset.copy); } catch { /* clipboard blocked: the link still works */ }
        t.innerHTML = icon('check'); t.classList.add('copied'); setTimeout(() => { t.innerHTML = icon('copy'); t.classList.remove('copied'); }, 1400);
        return;
      }
      if (t.dataset.project) return this.pick(t.dataset.project);
      if (t.dataset.goto) { const [p, arg] = t.dataset.goto.split(':'); return this.open(p as PanelId, arg); }
      if (t.dataset.panel) this.open(t.dataset.panel as PanelId);
      if (t.dataset.action) this.onAction(t.dataset.action);
    });
    // mono labels scramble into place on hover
    this.root.addEventListener('pointerover', (e) => {
      const host = (e.target as HTMLElement).closest<HTMLElement>('a, button, .sp-label');
      host?.querySelectorAll<HTMLElement>('[data-scramble]').forEach(scramble);
    });
    // the project index: a preview follows the cursor over the names (mouse only)
    this.root.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const float = this.body.querySelector<HTMLElement>('.wk-float');
      if (!float) return;
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-project]');
      if (!row || row.getAttribute('aria-selected') === 'true') { float.classList.remove('on'); return; }
      const p = PROJECTS.find((x) => x.id === row.dataset.project);
      if (p && float.dataset.id !== p.id) { float.dataset.id = p.id; float.style.setProperty('--acc', look(p).accent); float.innerHTML = visual(p); }
      const r = this.root.getBoundingClientRect();
      float.style.transform = `translate(${e.clientX - r.left + 24}px, ${e.clientY - r.top + this.root.scrollTop - 70}px)`;
      float.classList.add('on');
    });
    this.root.addEventListener('pointerleave', () => this.body.querySelector('.wk-float')?.classList.remove('on'));
    // the project index works like tabs: arrow keys move between projects
    this.root.addEventListener('keydown', (e) => {
      const tab = (e.target as HTMLElement).closest<HTMLElement>('[data-project]');
      if (!tab || !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      const tabs = [...this.root.querySelectorAll<HTMLElement>('[data-project]')];
      const k = (tabs.indexOf(tab) + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[k].focus(); this.pick(tabs[k].dataset.project as string);
    });
    // phones: drag the sheet down by its top to dismiss it
    let drag: { y: number; id: number } | null = null;
    this.root.addEventListener('pointerdown', (e) => {
      if (innerWidth > 700 || !(e.target as HTMLElement).closest('.sp-top, .sp-grab') || (e.target as HTMLElement).closest('button')) return;
      drag = { y: e.clientY, id: e.pointerId }; this.root.setPointerCapture(e.pointerId); this.root.style.transition = 'none';
    });
    this.root.addEventListener('pointermove', (e) => { if (drag && e.pointerId === drag.id) this.root.style.transform = `translateY(${Math.max(0, e.clientY - drag.y)}px)`; });
    this.root.addEventListener('pointerup', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dy = e.clientY - drag.y; drag = null; this.root.style.transition = ''; this.root.style.transform = '';
      if (dy > 90) this.close();
    });
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.current) { e.stopPropagation(); this.close(); } }, true);
    // the Back button (or gesture) closes the panel instead of leaving the site
    addEventListener('popstate', () => { if (this.pushed) { this.pushed = false; if (this.current) this.close(); } });
  }

  open(id: PanelId, arg?: string) {
    if (!this.current) {
      this.lastFocus = document.activeElement;
      if (!this.pushed) { history.pushState({ ...(history.state as object | null), panel: true }, ''); this.pushed = true; }
    }
    this.current = id;
    const chapter = chapterOf(id);
    this.body.innerHTML = `<div class="sp-grab" aria-hidden="true"></div>
      <header class="sp-top"><span class="sp-mark">Salman’s Studio</span><nav class="sp-index" aria-label="Sections">${CHAPTERS.map(([c, name]) => `<button data-panel="${c}"${c === chapter ? ' aria-current="page"' : ''}><span>${num(c)}</span><span data-scramble>${name}</span></button>`).join('')}</nav></header>
      <article class="sp-page sp-${id}">${RENDER[id](arg)}</article>`;
    this.root.dataset.page = id;
    this.root.hidden = false;
    this.root.scrollTop = 0;
    this.root.classList.remove('swap'); void this.root.offsetWidth; this.root.classList.add('swap');
    requestAnimationFrame(() => this.root.classList.add('open'));
    clearInterval(this.clock);
    if (this.body.querySelector('[data-clock]')) this.clock = window.setInterval(() => this.body.querySelectorAll('[data-clock]').forEach((el) => { el.textContent = dhakaTime(); }), 20000);
    (document.getElementById('panel-close') as HTMLElement).focus({ preventScroll: true });
  }

  /** Show one project in the Work browser. */
  private pick(id: string) {
    const k = PROJECTS.findIndex((x) => x.id === id), view = this.body.querySelector<HTMLElement>('.wk-view');
    if (k < 0 || !view) return;
    this.body.querySelectorAll<HTMLElement>('[data-project]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.project === id)));
    this.body.querySelector('.wk-float')?.classList.remove('on');
    view.classList.remove('in'); void view.offsetWidth;
    view.innerHTML = projectView(PROJECTS[k], k);
    view.classList.add('in');
    if (innerWidth <= 1100) view.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  close() {
    if (!this.current) return;
    this.current = null;
    clearInterval(this.clock);
    this.root.classList.remove('open');
    this.root.hidden = true;
    if (this.pushed) { this.pushed = false; history.back(); }
    (this.lastFocus as HTMLElement | null)?.focus?.({ preventScroll: true });
    this.onClose();
  }

  get isOpen() { return this.current !== null; }
}
